import { $, _, Backbone } from "js/vendor";
import { app } from "js/namespaces";
import Adapter from "js/core/storage/adapter";
import Thumbnails from "js/core/models/thumbnails";
import { UndoType } from "common/constants";
import { isElectron } from "js/config";
import { ds } from "./dataService";
import StorageModel from "../storage/storageModel";
import { downloadFromUrl, trackActivity } from "../utilities/utilities";
import log from "../logger";
import CommandsAdapter from "../storage/commandsAdapter";
import Profiler from "../profiler";
import ConnectionToggleAdapter from "../storage/connectionToggleAdapter";
import renderReactRoot from "js/react/renderReactRoot";
import LoadingDialog from "js/react/components/Dialogs/LoadingDialog";
import { setGlobal } from "reactn";
import { THUMBNAIL_SIZES } from "common/constants";
import { FeatureType } from "./features";
import DummyAdapter from "../storage/dummyAdapter";
import Api from "js/core/api";

const profiler = Profiler.create({
    name: "Slide",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 400
});

const nonTextKeys = {
    content_type: true,
    content_value: true
};

// These properties will not be saved as revision commands.
const SLIDE_COMMAND_BLACKLIST = [
    "presentationId",
    "thumbnails",
    "modifiedAt",
    "slideNotes",
    "isSkipped",
    "libraryItemId",
    "isGallerySlide"
];

const SOURCE = {
    local: 0,
    remote: 1,
    server: 2,
    initialize: 3
};

const MIGRATION_VERSION = 2;

const Slide = StorageModel.extend({

    root: "slides",
    profiler: profiler,
    readonly: isElectron,
    hasChanges: false,

    createAdapter: function(options) {
        const commandAdapter = new CommandsAdapter(
            Object.assign({
                writablePlainProperties: ["presentationId"],
                plainProperties: SLIDE_COMMAND_BLACKLIST
            }, options, { userId: app.user && app.user.id })
        );
        if (isElectron) {
            return new ConnectionToggleAdapter(Object.assign({
                onlineAdapter: commandAdapter
            }, options));
        } else {
            return commandAdapter;
        }
    },

    destroyHistory() {
        this.adapter.destroyHistory();
    },

    initialize: function(model, options) {
        this.type = "Slide";
        this.destroyed = false;
        this.dataStateIndex = 0;
        this.once("load", () => {
            this.dataStateIndex = 0;
            this._updateDataState({ source: SOURCE.initialize });
        });
        this.presentation = options.presentation;
    },

    getElements: function() {
        const states = this.get("states");
        if (!states) {
            return null;
        }
        return states[this.dataStateIndex] || null;
    },

    hasNextState: function() {
        const states = this.get("states");
        return states && this.dataStateIndex + 1 < states.length;
    },

    nextState: function() {
        if (!this.hasNextState()) {
            return;
        }
        this.dataStateIndex++;
        this._updateDataState({ source: SOURCE.local });
    },

    generateLoadPromise() {
        return StorageModel.prototype.generateLoadPromise.call(this).then(() => {
            const loaders = [];
            if (ds.assets) {
                loaders.push(ds.assets.loadAssetsForSlide(this));
            }
            return Promise.all(loaders);
        }).then(() => {
            return this;
        });
    },

    setDataState: function(index) {
        if (!this.loaded) {
            return;
        }
        this.dataStateIndex = index;
        this._updateDataState({ source: SOURCE.local });
    },

    debug: function() {
        console.log("-----------------------------------------------");
        console.log("Slide:" + this.id + " index: " + ds.selection.presentation.getSlideIndex(this.id));
        console.log("Template: " + this.get("template_id"));
        console.log(JSON.stringify(this.get("states")[0], null, 2));
    },

    handleRemoteChange: function(type, data) {
        const changeSet = StorageModel.prototype.handleRemoteChange.call(this, type, data);

        // The initialize type is handled when the slide loads, so skip double firing here.
        if (type === Adapter.TYPE.initialize) {
            return;
        }

        if (changeSet.hasUpdates) {
            this._updateDataState({ source: this._isDataStateChangeSet(changeSet) ? SOURCE.remote : SOURCE.server });
        }
    },

    update: function(attrs, options = {}, fromMigration) {
        if (this.has("libraryItemId") &&
            !options.confirmChanges &&
            app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, this.presentation.getWorkspaceId()) &&
            !(this.adapter instanceof DummyAdapter)) {
            this.mainAdapter = this.adapter;
            this.adapter = new DummyAdapter();
            this.originalState = _.cloneDeep(this.attributes);
            this.hasStartedEditing = true;
            this.trigger("hasStartedEditing");
        }

        if (window.isPlayer && !fromMigration) {
            log.warn("Trying to commit when data should be read only");
            options = Object.assign({}, options, { save: false });
        }
        const changeSet = StorageModel.prototype.update.call(this, attrs, options);
        if (!options.isCommit && changeSet.hasUpdates && this._isDataStateChangeSet(changeSet)) {
            this._updateDataState(Object.assign(options, { source: SOURCE.local }));
        }

        if (changeSet.hasUpdates) {
            this.hasChanges = true;
        }

        return changeSet;
    },

    getLibraryItem() {
        const orgId = ds.selection.presentation.get("orgId");
        const libraryItemId = this.get("libraryItemId");
        if (!libraryItemId) {
            throw new Error("This slide is not a shared slide and cannot access library metadata.");
        }
        return ds.teams.defaultTeamForOrg(orgId).libraryItems.get(libraryItemId);
    },

    applyNewState: function(newState) {
        const currentTemplate = this.get("template_id");
        let changeSet = this.update(newState, { removeMissingKeys: true, needsMigration: true, save: true, confirmChanges: true });
        if (!changeSet.hasUpdates) {
            return;
        }
        this.set("template_id", this.get("template_id"));
        if (app.undoManager) {
            app.undoManager.set(UndoType.SLIDE_DATA, this.id, changeSet.original, changeSet.update);
        }

        // Special case updating for a template such as when the slide variation is updated
        if (this.originalState.template_id !== currentTemplate) {
            this.model.template_id = this.originalState.template_id;
        }
    },

    onAfterSaveSharedSlide() {
        const props = {
            slide_id: this.id,
            template_name: this.get("template_id"),
        };
        trackActivity("SharedSlideLibrary", "SlideContentEdited", null, null, props, { audit: true });
    },

    _isDataStateChangeSet: function(changeSet) {
        return "template_id" in changeSet.original || "layout" in changeSet.original || "states" in changeSet.original;
    },

    _reloadDataState: function(options) {
        this.dataState = {
            template_id: this.get("template_id"),
            layout: _.cloneDeep(this.get("layout")),
            elements: _.cloneDeep(this.getElements()),
            version: this.get("version")
        };
        if (!options.silent) {
            this.trigger("updateDataState", this, Object.assign(options, {
                remoteChange: options.source === "remote",
                initialize: options.source === "initialize"
            }));
        }
    },

    _updateDataState: function(options) {
        switch (options.source) {
            case SOURCE.local:
                log.info(log.COLLABORATION)("local change triggered", this.id);
                this._reloadDataState(options);
                break;
            case SOURCE.remote:
                log.info(log.COLLABORATION)("remote change triggered", this.id);
                this._reloadDataState(options);
                break;
            case SOURCE.initialize:
                log.info(log.COLLABORATION)("init change triggered", this.id);
                this._reloadDataState(options);
                break;
            case SOURCE.server:
                log.info(log.COLLABORATION)("server change triggered", this.id);
                break;
            default:
                log.warn("Source unknown: " + options.source);
                break;
        }
    },

    /**
	 * This will push the dataState to the model.
	 * @param silent If true, no events will be emitted. this can prevent feedback loops in cases where we
     * don't want the display to update.
	 */
    commit: function(options = {}) {
        if (window.isRenderer || window.isPlayer) {
            log.warn("Trying to commit when data should be read only");
            options = Object.assign({}, options, { save: false });
        }

        const data = Object.assign({}, this.attributes, this.dataState);
        //no such thing as elements in the stored model.
        delete data.elements;

        //elements is instead... states!
        data.states = data.states.slice();
        data.states[this.dataStateIndex] = this.dataState.elements;

        const changeSet = this.update(data, { removeMissingKeys: true, isCommit: true });

        if (!changeSet.hasUpdates) {
            return this.dataState;
        }

        if (app.undoManager && options.undo !== false) {
            app.undoManager.set(UndoType.SLIDE_DATA, this.id, changeSet.original, changeSet.update);
        }

        return this.dataState;
    },

    discardChanges: function() {
        this._updateDataState({ source: SOURCE.local, silent: true });
    },

    destroy: function() {
        this.disconnect();
        this.isDeleting = true;
        this.destroyed = true;

        Backbone.Model.prototype.destroy.apply(this, arguments);
    },

    finishedEditing: function() {
        if (this.hasChanges) {
            const timestamp = new Date().getTime();

            StorageModel.prototype.update.call(this, { modifiedAt: timestamp });

            if (this.has("libraryItemId")) {
                // when shared slide is updated, update libraryItem metadata to reflect when and who updated
                let libraryItem = this.getLibraryItem();
                libraryItem.update({});
            }

            if (this.presentation) {
                // If the slide is the first slide of its presentation
                if (this.presentation.getSlideIndex(this.id) === 0) {
                    this.presentation.update({ firstSlideModifiedAt: timestamp });
                }

                if (app.notificationsService) {
                    app.notificationsService.notifyOnSlideEdited(this.presentation.id, this.id)
                        .catch(err => console.error(err));
                }
            }

            this.hasChanges = false;
        }
    },

    hasMultipleStages() {
        return this.get("template_id") === "textgrid_carousel";
    },

    getPlaybackStageCount() {
        if (this.hasMultipleStages()) {
            return this.dataState.elements.primary.items.length;
        }
        return 1;
    },

    async getThumbnailUrl(presentationId = null, size = "small", stageIndex = 0) {
        log.info(log.SLIDE)(`getThumbnailUrl() presentationId: ${presentationId} size: ${size} stageIndex: ${stageIndex}`);

        if (!THUMBNAIL_SIZES[size]) {
            throw new Error(`Unknown thumbnail size ${size}`);
        }

        await this.loadPromise;

        return Thumbnails.getSignedUrlAndLoad(this.id, this.get("modifiedAt"), presentationId, THUMBNAIL_SIZES[size].suffix, stageIndex);
    },

    exportSlideToJpeg($parentEl) {
        trackActivity("Presentation", "JPEGExport", null, null, {}, { audit: true });
        setGlobal({
            isLoadingDialogOpen: true
        });
        let loadingDialogProps = { title: "Exporting Slide to JPEG..." };
        const $loadingDialogContainer = $.div("loading_dialog_container");
        $parentEl.addEl($loadingDialogContainer);
        const unmount = renderReactRoot(
            LoadingDialog,
            loadingDialogProps,
            $loadingDialogContainer[0]
        );

        Api.jpegs.get({
            slideId: this.id,
            presentationId: this.presentation.id,
            hideWatermark: app.user.features.isFeatureEnabled(FeatureType.REMOVE_BAI_BRANDING, this.presentation.getWorkspaceId())
        })
            .then(({ urls }) => urls.map((url, idx) => downloadFromUrl(url, `Slide ${this.getIndex() + 1}${idx !== 0 ? `-${idx}` : ""}.jpeg`)))
            .then(() => {
                setGlobal({
                    isLoadingDialogOpen: false
                });
                unmount();
            })
            .catch(err => {
                setGlobal({
                    isLoadingDialogOpen: false
                });
                unmount();
                log.error(err);
            });
    },

    getIndex() {
        return ds.selection.presentation.getSlideIndex(this.id);
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        // Loading thumbnails of all sizes
        await Promise.all(Object.keys(THUMBNAIL_SIZES).map(size => this.getThumbnailUrl(this.presentation.id, size)));
        return attrs;
    },

    toText: function() {
        const text = [];

        function toText(obj) {
            if (typeof obj === "string" && obj !== "") {
                text.push(obj);
            } else if (_.isObject(obj)) {
                Object.keys(obj).forEach(key => {
                    if (!nonTextKeys[key]) {
                        toText(obj[key]);
                    }
                });
            }
        }

        toText(this.get("states"));
        text.push(this.get("slide_notes"));
        return text.join(" ");
    },

    isLibrarySlide: function() {
        return this.has("libraryItemId");
    },

    isLibrarySlideInCurrentUserOrg: function() {
        const orgId = this.presentation.get("orgId");
        if (this.presentation.getWorkspaceId() !== "personal") {
            const defaultTeam = ds.teams.defaultTeamForOrg(orgId);
            if (Array.isArray(defaultTeam.get("sharedResources")) && defaultTeam.get("sharedResources")[1]) {
                if (defaultTeam.get("sharedResources")[1][this.get("libraryItemId")]) {
                    return true;
                }
            }
        }
        return false;
    },
});

const Slides = Backbone.Collection.extend({
    model: Slide,

    url: function() {
        return "slides";
    },

    initialize: function() {
        this.type = "Slides";
    }
});

export { Slide, Slides, SLIDE_COMMAND_BLACKLIST };
